import React, { useEffect, useRef, useState } from 'react';
import { PaymentInfo } from '../types';
import { CheckCircle2, QrCode, Smartphone, ShieldCheck, X, Clock, RefreshCw, Lock, Loader2 } from 'lucide-react';
import { api, getCsrfToken } from '../api';

interface PaymentModalProps {
  payment: PaymentInfo | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({ payment, onClose, onSuccess }) => {
  const [countdown, setCountdown] = useState(7200);
  const [polling, setPolling] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 轮询支付状态
  useEffect(() => {
    if (!payment) return;
    setCountdown(7200);
    setSuccess(false);
    setError('');
    setPolling(true);

    const poll = async () => {
      try {
        const data = await api<{ status: string }>('pay_status', { orderNo: payment.orderNo });
        if (data.status === 'paid' || data.status === 'refunded') {
          setSuccess(true);
          setPolling(false);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          setTimeout(onSuccess, 1600);
        }
      } catch { /* 网络抖动忽略 */ }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);

    const timer = setInterval(() => setCountdown((p) => (p > 0 ? p - 1 : 0)), 1000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment?.orderNo]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  if (!payment) return null;

  const simulatePay = async () => {
    setSimulating(true);
    setError('');
    try {
      await api('mock_pay_success', { orderNo: payment.orderNo }, 'POST');
      setSuccess(true);
      setPolling(false);
      setTimeout(onSuccess, 1600);
    } catch (ex: any) {
      setError(ex.message || '模拟支付失败');
    } finally {
      setSimulating(false);
    }
  };

  const formatTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 relative my-8">
        <button onClick={onClose} className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100">
          <X className="w-5 h-5" />
        </button>

        {/* 头部 */}
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-lg shadow-md shadow-emerald-600/20">微</div>
          <div>
            <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
              <span>微信支付官方收银台</span>
              <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-200">
                {payment.pay_mode === 'mock' ? '联调模式' : 'V3 验签中'}
              </span>
            </h3>
            <p className="text-[11px] text-slate-400">商户: 长顺县游泳协会</p>
          </div>
        </div>

        {!success ? (
          <div className="space-y-6 pt-4">
            {/* 金额 */}
            <div className="bg-slate-50 p-4 rounded-2xl text-center border border-slate-100 space-y-1">
              <span className="text-xs text-slate-500">{payment.title}</span>
              <div className="text-3xl font-black text-slate-900 tracking-tight">
                <span className="text-xs text-slate-500 font-normal">¥</span> {payment.amount.toFixed(2)}
              </div>
              <div className="flex items-center justify-center gap-1 text-[11px] text-slate-400 pt-1">
                <Clock className="w-3.5 h-3.5 text-amber-500" />
                <span>二维码有效期剩余: <strong className="font-mono text-amber-600">{formatTime(countdown)}</strong></span>
              </div>
              <div className="text-[10px] font-mono text-slate-400 pt-1">订单号: {payment.orderNo}</div>
            </div>

            {/* 二维码 */}
            <div className="text-center space-y-3">
              <div className="p-4 bg-slate-900 rounded-2xl inline-block border-4 border-emerald-500/30 shadow-xl">
                <div className="w-48 h-48 bg-white p-2 rounded-xl flex items-center justify-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(payment.code_url)}`}
                    alt="微信支付二维码"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-600">
                请使用手机 <strong className="text-emerald-600">微信“扫一扫”</strong> 扫描上方二维码完成支付
              </p>
              {polling && (
                <p className="flex items-center justify-center gap-1.5 text-[11px] text-sky-600 font-bold">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />正在检测支付结果...
                </p>
              )}
            </div>

            {error && <p className="text-xs text-red-600 font-semibold bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}

            {/* 模拟支付按钮（仅联调模式显示） */}
            {payment.pay_mode === 'mock' && (
              <div className="space-y-2">
                <button
                  onClick={simulatePay}
                  disabled={simulating}
                  className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-lg shadow-emerald-600/25 flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                >
                  {simulating ? (<><RefreshCw className="w-4 h-4 animate-spin" /><span>处理中...</span></>) : (<><Lock className="w-4 h-4" /><span>模拟支付成功（联调模式，不产生真实扣款）</span></>)}
                </button>
                <p className="text-center text-[10px] text-slate-400">联调模式说明：正式上线时在 config.php 将 PAY_MODE 改为 'real' 并配置微信支付商户号后，此按钮将自动隐藏</p>
              </div>
            )}

            <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>256-bit SSL 加密传输 · 微信支付资金受银行全额监管</span>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center space-y-4 animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-lg shadow-emerald-100">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <div className="space-y-1">
              <h4 className="text-xl font-black text-slate-900">支付完成！</h4>
              <p className="text-xs text-slate-500 font-mono">{payment.orderNo}</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-2xl text-left text-xs space-y-2 text-slate-600 border border-slate-100">
              <div className="flex justify-between"><span>支付金额</span><span className="font-bold text-emerald-600">¥{payment.amount.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>业务类型</span><span>{payment.type === 'membership_fee' ? '会员会费' : '赛事报名'}</span></div>
              <div className="flex justify-between"><span>后续流程</span><span>等待协会管理员审核</span></div>
            </div>
            <p className="text-xs text-emerald-600 font-bold">界面即将自动跳转...</p>
          </div>
        )}
      </div>
    </div>
  );
};
